import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackCTA } from "@/lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ_ITEMS = [
  { id: "dejar-usar", q: "¿Es necesario dejar de usar inteligencia artificial?", a: "No. El objetivo de la auditoría no es frenar la innovación, sino permitir un uso más seguro, documentado y sostenible de las herramientas ya implantadas o previstas. Muchas clínicas continúan usando exactamente las mismas herramientas; simplemente añaden los controles, documentos y protocolos que faltaban." },
  { id: "interfiere", q: "¿La revisión interfiere en el funcionamiento diario de la clínica?", a: "No debería. El análisis se basa en entrevistas breves, revisión documental y mapeo de procesos, con el mínimo impacto operativo razonable. La mayor parte del trabajo se realiza de forma asíncrona, sin interrumpir la actividad clínica ni administrativa." },
  { id: "chatbots", q: "¿También revisáis chatbots, CRM y automatizaciones de marketing?", a: "Sí. Es habitual que los mayores riesgos no estén solo en un simulador de resultados, sino en el conjunto de herramientas, mensajes automáticos, formularios de captación y circuitos internos de seguimiento. El marketing con IA es una de las áreas más sensibles por la normativa de publicidad sanitaria." },
  { id: "parcial", q: "¿Puedo pedir solo una revisión parcial de un área concreta?", a: "Sí. Puede plantearse una revisión enfocada en consentimientos, imágenes, marketing, protocolos internos o herramientas concretas. Muchas clínicas comienzan con una revisión de consentimiento informado vinculado a IA o con el análisis del uso de fotografías de pacientes en simuladores." },
  { id: "normativa", q: "¿Qué normativa cubre exactamente esta auditoría?", a: "El trabajo toma en consideración el RGPD (Reglamento UE 2016/679), la LOPDGDD (Ley Orgánica 3/2018), el Reglamento Europeo de IA (AI Act, Reglamento UE 2024/1689), la Ley 41/2002 de autonomía del paciente, el Real Decreto 1030/2006 de cartera de servicios del SNS, y la normativa española de publicidad sanitaria." },
  { id: "tiempo", q: "¿Cuánto tiempo tarda el proceso desde el diagnóstico hasta el informe?", a: "Depende del alcance acordado. Un diagnóstico inicial con informe de riesgos puede completarse en una o dos semanas. Una auditoría completa con protocolos y documentos suele requerir entre tres y seis semanas, según la complejidad de la estructura y el número de herramientas." },
];

export function FaqSection() {
  return (
    <section id="faq" className="section-darker" data-testid="faq-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="section-title">Preguntas frecuentes</h2>
          <p className="section-subtitle mx-auto mt-4">
            Dudas habituales cuando una clínica empieza a revisar el uso de IA con enfoque legal y operativo.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {FAQ_ITEMS.map((faq) => (
              <AccordionItem key={faq.id} value={faq.id} className="faq-item">
                <AccordionTrigger className="faq-trigger" data-testid={`faq-trigger-${faq.id}`}>{faq.q}</AccordionTrigger>
                <AccordionContent className="faq-content">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

export function FinalCtaSection({ scrollToSection }) {
  return (
    <section className="section-dark" data-testid="final-cta-section">
      <div className="container-custom">
        <div className="lead-magnet max-w-3xl mx-auto">
          <span className="eyebrow mb-4 inline-flex">El momento es ahora</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Si tu clínica ya usa IA, este es el momento de poner orden
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Una revisión temprana suele costar mucho menos que corregir después flujos, documentos o mensajes que ya están generando riesgo para la clínica o para los pacientes.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button onClick={() => { trackCTA("final_diagnostico"); scrollToSection('diagnostico'); }} className="btn-primary text-base" data-testid="final-cta-button">
              <Lock className="w-4 h-4" />
              Quiero revisar el uso de IA en mi clínica
            </Button>
            <Button onClick={() => scrollToSection('precios')} variant="outline" className="btn-secondary text-base" data-testid="final-pricing-button">
              Ver precios
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
